import { useQuery, UseQueryResult } from '@tanstack/react-query';
import axios from 'lib/axios';
import React, { ReactNode, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';

type SearchContextValue = UseQueryResult<null | any[]> & {
  query: string;
  setQuery: (value: string) => void;
};

interface SearchProviderProps {
  searchKey: string;
  searchPath: string;
  queryKey?: string;
  searchMode?: 'always' | 'query';
  searchParams?: object;
  urlParamKey?: string;
  children?: ReactNode;
}

const SearchContext = React.createContext<SearchContextValue | undefined>(
  undefined
);

export const useSearchContext = () => {
  const value = useContext(SearchContext);
  if (value === undefined) {
    throw Error('useSearchContext can not be used outside a SearchProvider');
  }
  return value;
};

export default function SearchProvider({
  searchKey,
  searchPath,
  queryKey,
  searchMode = 'always',
  searchParams,
  urlParamKey = 'q',
  children,
}: SearchProviderProps) {
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  const query = urlSearchParams.get(urlParamKey) || '';

  const queryFn = async (query: string) => {
    if (searchMode === 'query' && query.trim().length === 0) return null;
    let params = { q: query };
    if (searchParams) params = { ...params, ...searchParams };
    const { data } = await axios.get(searchPath, { params });
    return data[searchKey];
  };

  const queryResult = useQuery({
    queryKey: [queryKey || `search-${searchKey}`, query],
    queryFn: () => queryFn(query),
  });

  const setQuery = (value: string) => {
    setUrlSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set(urlParamKey, value);
        return params;
      },
      { replace: true }
    );
  };

  return (
    <SearchContext.Provider
      value={{
        query,
        setQuery,
        ...queryResult,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
}
